"use client";

/**
 * `listing.close` for the listing on screen (the drawer, the /elan page):
 * `{id, ms, by, rec?, pos?}`, `ms` counted only while the tab is visible
 * (lib/listing-signals createDwell).
 *
 * Sent when the listing leaves the screen (`by: "close"`: the drawer
 * closes or swaps to another listing, the page unmounts, or the route
 * changes under a drawer that stays open), when the tab is hidden
 * (`"hidden"`; phones often never fire pagehide for a tab they later
 * discard) and on pagehide (`"pagehide"`). Each send carries the visible
 * time since the previous one, so the `ms` of one open add up
 * (services/rec_events sums them).
 */
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { captureEvent } from "@/lib/tracking";
import { createDwell } from "@/lib/listing-signals";
import { withRec, type RecRef } from "@/lib/rec-client";

/** Below this a send is noise (Strict Mode's mount-unmount-mount in
 *  development, a mis-tap that closed the drawer at once). */
const MIN_MS = 100;

export function useListingDwell(id: string | null, rec?: RecRef | null): void {
  const pathname = usePathname();
  const recRef = useRef(rec);
  useEffect(() => { recRef.current = rec; }, [rec]);

  useEffect(() => {
    if (!id) return;
    const dwell = createDwell(() => Date.now(), document.visibilityState === "visible");
    const flush = (by: "close" | "hidden" | "pagehide") => {
      const ms = dwell.take();
      // The page is unloading on pagehide: a beacon survives it.
      if (ms >= MIN_MS) captureEvent("listing.close", withRec({ id, ms, by }, recRef.current), { beacon: by === "pagehide" });
    };
    const onVisibility = () => {
      const visible = document.visibilityState === "visible";
      if (!visible) {
        dwell.setVisible(false);
        flush("hidden");
      } else {
        dwell.setVisible(true);
      }
    };
    const onPageHide = () => flush("pagehide");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      flush("close");
    };
    // The pathname: a route change under a drawer that stays open closes
    // the visit there and starts a new one.
  }, [id, pathname]);
}
